// src/components/AnalyticsOrgDetailPanel.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend
} from 'recharts';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { formatDateTick, CHART_COLORS, TOOLTIP_STYLE } from './analyticsChartUtils';

const getOrgAnalyticsDetailCallable = httpsCallable(functions, 'getOrgAnalyticsDetail');

// Cache detail payloads per org+range so collapse/expand doesn't refetch
const detailCache = new Map();

const AXIS_PROPS = {
  stroke: CHART_COLORS.axis,
  fontSize: 11,
  tickLine: false,
};

function DetailChartCard({ title, subtitle, children }) {
  return (
    <div className="analytics-detail-card">
      <div className="analytics-chart-title">{title}</div>
      {subtitle && <div className="analytics-chart-subtitle">{subtitle}</div>}
      {children}
    </div>
  );
}

function TimeSeriesAxes() {
  return [
    <CartesianGrid key="grid" strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />,
    <XAxis
      key="x"
      dataKey="date"
      {...AXIS_PROPS}
      tickFormatter={formatDateTick}
      interval="preserveStartEnd"
      minTickGap={32}
    />,
    <YAxis key="y" {...AXIS_PROPS} allowDecimals={false} width={30} />,
  ];
}

function ChangesChart({ data, entityLabel }) {
  const hasChanges = data.some((d) => d.added > 0 || d.deleted > 0);
  if (!hasChanges) {
    return <div className="analytics-detail-none">No {entityLabel} changes in this period</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
        {TimeSeriesAxes()}
        <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={formatDateTick} />
        <Legend wrapperStyle={{ fontSize: '0.75rem' }} iconSize={10} />
        <Bar dataKey="added" name="Added" fill={CHART_COLORS.positive} radius={[3, 3, 0, 0]} maxBarSize={14} />
        <Bar dataKey="deleted" name="Deleted" fill={CHART_COLORS.negative} radius={[3, 3, 0, 0]} maxBarSize={14} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function FunnelBars({ funnel }) {
  const stages = [
    { label: 'Tokens created', value: funnel.tokensCreated, color: '#1e40af' },
    { label: 'Requests submitted', value: funnel.requestsSubmitted, color: '#3b82f6' },
    { label: 'Approved', value: funnel.approved, color: '#60a5fa' },
    { label: 'Rejected', value: funnel.rejected, color: '#93c5fd' },
  ];
  const max = Math.max(...stages.map((s) => s.value), 1);
  const total = stages.reduce((acc, s) => acc + s.value, 0);
  if (total === 0) {
    return <div className="analytics-detail-none">No join activity in this period</div>;
  }
  return (
    <div className="analytics-funnel">
      {stages.map((stage) => (
        <div key={stage.label} className="analytics-funnel-row">
          <span className="analytics-funnel-label">{stage.label}</span>
          <div className="analytics-funnel-track">
            <div
              className="analytics-funnel-bar"
              style={{ width: `${(stage.value / max) * 100}%`, background: stage.color }}
            />
            <span className="analytics-funnel-count">{stage.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnalyticsOrgDetailPanel({ orgId, orgName, days }) {
  const cacheKey = `${orgId}:${days}`;
  const [detail, setDetail] = useState(detailCache.get(cacheKey) || null);
  const [loading, setLoading] = useState(!detailCache.has(cacheKey));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (detailCache.has(cacheKey)) {
      setDetail(detailCache.get(cacheKey));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getOrgAnalyticsDetailCallable({ orgId, days })
      .then((result) => {
        if (cancelled) return;
        if (result.data.success) {
          detailCache.set(cacheKey, result.data);
          setDetail(result.data);
        } else {
          setError(result.data.message || 'Failed to load org details.');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load org details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [cacheKey, orgId, days]);

  if (loading) {
    return (
      <div className="analytics-loading" style={{ padding: '2rem' }}>
        <div className="analytics-spinner"></div>
        <span>Loading {orgName} details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-alert analytics-alert-danger" style={{ marginBottom: 0 }}>
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <div>{error}</div>
      </div>
    );
  }

  if (!detail) return null;

  const comms = detail.communications;
  const wishes = detail.birthdayWishes;
  const attendance = detail.attendance;
  const hasLogins = detail.dailyLogins.some((d) => d.count > 0);
  const hasWishes = wishes.total > 0;

  return (
    <div className="analytics-detail">
      <div className="analytics-detail-grid">
        {/* Daily logins */}
        <DetailChartCard title="Users by last-active day">
          {hasLogins ? (
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={detail.dailyLogins} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                {TimeSeriesAxes()}
                <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={formatDateTick} formatter={(v) => [v, 'Users']} />
                <Line type="monotone" dataKey="count" stroke={CHART_COLORS.series} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="analytics-detail-none">No user activity in this period</div>
          )}
        </DetailChartCard>

        {/* Attendance */}
        <DetailChartCard
          title="Attendance"
          subtitle={attendance.recordCount > 0 ? `${attendance.recordCount} records logged` : null}
        >
          {attendance.daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={attendance.daily} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />
                <XAxis dataKey="date" {...AXIS_PROPS} tickFormatter={formatDateTick} interval="preserveStartEnd" minTickGap={32} />
                <YAxis {...AXIS_PROPS} allowDecimals={false} width={35} />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={formatDateTick} formatter={(v) => [v, 'Attended']} />
                <Line type="monotone" dataKey="total" stroke={CHART_COLORS.series} strokeWidth={2} dot={{ r: 3, fill: CHART_COLORS.series }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="analytics-detail-none">No attendance logged in this period</div>
          )}
        </DetailChartCard>

        {/* Member changes */}
        <DetailChartCard
          title="Member changes"
          subtitle={`${detail.growth.memberCount} members currently`}
        >
          <ChangesChart data={detail.growth.members} entityLabel="member" />
        </DetailChartCard>

        {/* Family changes */}
        <DetailChartCard
          title="Family changes"
          subtitle={`${detail.growth.familyCount} families currently`}
        >
          <ChangesChart data={detail.growth.families} entityLabel="family" />
        </DetailChartCard>

        {/* Communications */}
        <DetailChartCard
          title="Communications sent"
          subtitle={
            comms.totals.messages > 0
              ? `${comms.totals.messages} sent (${comms.totals.announcements} announcements, ${comms.totals.automations} automated) · ${comms.totals.delivered.toLocaleString()} delivered · ${comms.totals.failed} failed`
              : null
          }
        >
          {comms.totals.messages > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={comms.daily} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  {TimeSeriesAxes()}
                  <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={formatDateTick} formatter={(v) => [v, 'Messages']} />
                  <Bar dataKey="count" fill={CHART_COLORS.series} radius={[3, 3, 0, 0]} maxBarSize={14} />
                </BarChart>
              </ResponsiveContainer>
              <table className="analytics-mini-table">
                <thead>
                  <tr><th>Recent</th><th>Type</th><th className="right">Delivered</th></tr>
                </thead>
                <tbody>
                  {comms.recent.slice(0, 5).map((c, idx) => (
                    <tr key={idx}>
                      <td className="analytics-mini-title">{c.title}</td>
                      <td>{c.type}</td>
                      <td className="right">{c.stats.delivered}/{c.stats.targeted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="analytics-detail-none">No communications sent in this period</div>
          )}
        </DetailChartCard>

        {/* Birthday wishes */}
        <DetailChartCard
          title="Birthday wishes"
          subtitle={hasWishes ? `${wishes.total} wishes from ${wishes.uniqueSenders} senders` : null}
        >
          {hasWishes ? (
            <>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={wishes.daily} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  {TimeSeriesAxes()}
                  <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={formatDateTick} formatter={(v) => [v, 'Wishes']} />
                  <Bar dataKey="count" fill={CHART_COLORS.series} radius={[3, 3, 0, 0]} maxBarSize={14} />
                </BarChart>
              </ResponsiveContainer>
              <div className="analytics-top-senders">
                {wishes.topSenders.slice(0, 5).map((s, idx) => (
                  <span key={idx} className="analytics-sender-chip">
                    {s.name} · {s.wishCount}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div className="analytics-detail-none">No birthday wishes in this period</div>
          )}
        </DetailChartCard>

        {/* Join funnel */}
        <DetailChartCard title="Join funnel" subtitle={`QR tokens → requests → review, last ${days} days`}>
          <FunnelBars funnel={detail.joinFunnel} />
        </DetailChartCard>
      </div>
    </div>
  );
}

export default AnalyticsOrgDetailPanel;
